import axios from 'axios'

// API tabanını dinamik çöz: Tauri'de istemci modunda localStorage'daki sunucu adresi
// (inova-api-base) kullanılır; yoksa localhost (sunucu/all-in-one). Tarayıcıda VITE env.
// Dinamik olması, localStorage'ın React mount'tan sonra set edilmesi durumunda da doğru çalışır.
export function resolveBaseURL(): string {
  if (typeof window !== 'undefined' && window.__TAURI__) {
    return localStorage.getItem('inova-api-base') || 'http://localhost:3001/api/v1'
  }
  return import.meta.env.VITE_API_BASE_URL || '/api/v1'
}

export const apiClient = axios.create({
  baseURL: resolveBaseURL(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
})

// Backend göreli varlık yolunu (ör. "/api/v1/products/:id/resim") <img src> için
// mutlak URL'e çevirir. İstemci/sunucu modunda istemci kendi API tabanını kullanır.
// data:/blob:/http(s): ile başlayanlar (yerel dosya önizleme, mock) olduğu gibi döner.
export function apiAssetUrl(path?: string | null): string | null {
  if (!path) return null
  if (/^(https?:|data:|blob:)/i.test(path)) return path
  const origin = resolveBaseURL().replace(/\/api\/v1\/?$/, '')
  return origin + (path.startsWith('/') ? path : `/${path}`)
}

// JWT interceptor — auth store'dan token oku
apiClient.interceptors.request.use((config) => {
  config.baseURL = resolveBaseURL() // istemci modunda dinamik sunucu adresi
  const stored = localStorage.getItem('inova-auth')
  if (stored) {
    try {
      const state = JSON.parse(stored)
      const token = state?.state?.accessToken
      if (token) config.headers.Authorization = `Bearer ${token}`
    } catch {
      // ignore
    }
  }
  return config
})

// Refresh işlemi devam ederken diğer istekleri kuyrukla
let isRefreshing = false
let refreshQueue: Array<(token: string) => void> = []

function onRefreshed(newToken: string) {
  refreshQueue.forEach((cb) => cb(newToken))
  refreshQueue = []
}

function doLogout(reason: string) {
  // Tanı: oturumun neden kapandığını masaüstü loguna yaz (idle değil, token kaynaklı).
  try {
    const w = window as { __TAURI__?: unknown }
    if (w.__TAURI__) {
      import('@tauri-apps/api/core')
        .then(({ invoke }) => invoke('frontend_log', { msg: `OTURUM KAPANDI (api) — ${reason}` }).catch(() => {}))
        .catch(() => {})
    }
  } catch { /* ignore */ }
  // eslint-disable-next-line no-console
  console.warn('[auth] logout:', reason)
  localStorage.removeItem('inova-auth')
  window.location.href = '/login'
}

// Hata interceptor — 401'de refresh dene, olmuyorsa logout
apiClient.interceptors.response.use(
  (res) => res.data,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login')) {
      originalRequest._retry = true

      // Refresh token'ı localStorage'dan al
      let refreshToken: string | null = null
      try {
        const stored = localStorage.getItem('inova-auth')
        if (stored) {
          const state = JSON.parse(stored)
          refreshToken = state?.state?.refreshToken ?? null
        }
      } catch { /* ignore */ }

      if (!refreshToken) {
        doLogout('refresh token yok')
        return Promise.reject(error)
      }

      // Zaten refresh yapılıyorsa kuyruğa al
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshQueue.push((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            resolve(apiClient(originalRequest))
          })
        })
      }

      isRefreshing = true
      try {
        const res = await axios.post(
          `${resolveBaseURL()}/auth/refresh`,
          { refreshToken },
        )
        const payload = (res.data as any)?.data ?? (res.data as any)
        const newAccessToken: string = payload?.accessToken
        // Backend token rotation yapıyor: yeni refresh token'ı da sakla, aksi halde
        // bir sonraki yenilemede eldeki refresh DB'de silinmiş olur → zorla logout.
        const newRefreshToken: string | undefined = payload?.refreshToken

        if (!newAccessToken) throw new Error('No access token in refresh response')

        // Store'u güncelle (access + rotation'lı refresh)
        try {
          const stored = localStorage.getItem('inova-auth')
          if (stored) {
            const parsed = JSON.parse(stored)
            parsed.state.accessToken = newAccessToken
            if (newRefreshToken) parsed.state.refreshToken = newRefreshToken
            localStorage.setItem('inova-auth', JSON.stringify(parsed))
          }
        } catch { /* ignore */ }

        onRefreshed(newAccessToken)
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return apiClient(originalRequest)
      } catch {
        // Refresh da başarısız — logout
        doLogout('refresh başarısız (token rotation/geçersiz)')
        return Promise.reject(error)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject({
      status: error.response?.status,
      message: error.response?.data?.message ?? error.message,
      data: error.response?.data,
    })
  },
)
